<template>
  <transition name="knock-guide-modal" appear>
    <div
      v-if="dialog"
      class="knock-guide-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="knock-guide-modal-title"
      @mousedown.self="onDismiss"
    >
      <div class="knock-guide-modal__card">
        <div class="knock-guide-modal__header">
          <span class="knock-guide-modal__eyebrow" v-if="eyebrow">
            {{ eyebrow }}
          </span>
          <button
            v-if="isDismissible"
            type="button"
            class="knock-guide-modal__close-btn"
            aria-label="Close guide"
            :disabled="isClosing"
            @click="onDismiss"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M15 5L5 15M5 5l10 10"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>

        <div class="knock-guide-modal__body">
          <h2 id="knock-guide-modal-title" class="knock-guide-modal__title">
            {{ title }}
          </h2>
          <!-- Knock authors the guide body as rich text; sanitizedBody is
               DOMPurify-sanitized before it reaches this v-html render. -->
          <div
            v-if="body"
            class="knock-guide-modal__text"
            v-html="sanitizedBody"
          ></div>
          <img
            v-if="imageUrl"
            class="knock-guide-modal__image"
            :src="imageUrl"
            :alt="imageAlt"
          />
        </div>

        <div v-if="ctaLabel" class="knock-guide-modal__footer">
          <button
            type="button"
            class="knock-guide-modal__cta"
            :disabled="isClosing"
            @click="onInteract"
          >
            {{ ctaLabel }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import * as Sentry from "@sentry/vue";
import DOMPurify from "dompurify";
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router/composables";

const props = defineProps({
  // The KnockGuide instance the current step belongs to. Null while no guide is
  // active (App.vue keeps the overlay mounted and passes null until fetch resolves).
  guide: { type: Object, default: null },
  // The current KnockGuideStep — carries `content` plus the SDK engagement
  // methods markAsSeen / markAsInteracted / markAsArchived. Null while inactive.
  step: { type: Object, default: null },
  dialog: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["dismiss", "interact"]);

const isClosing = ref(false);

const router = useRouter();

const content = computed(() => props.step?.content || {});

// Knock guide content schemas are versioned per-step (schema_key), so these
// fallbacks read the common modal fields rather than assuming one shape.
const title = computed(
  () =>
    content.value.title ||
    content.value.heading ||
    content.value.text ||
    ""
);
const body = computed(
  () =>
    content.value.body ||
    content.value.description ||
    content.value.subText ||
    ""
);
const eyebrow = computed(
  () => content.value.eyebrow || content.value.subtitle || ""
);
// Knock delivers the body as an HTML string; sanitize before v-html so rich text
// (bold/paragraphs) renders but script tags and event attributes are stripped.
const sanitizedBody = computed(() =>
  body.value ? DOMPurify.sanitize(body.value) : ""
);

// Knock's `single-action` modal schema (0.0.1) delivers the CTA as
// `primary_button.{text, action}`. Legacy content still uses the older field
// names, so those remain as fallbacks.
const ctaLabel = computed(
  () =>
    content.value.primary_button?.text ||
    content.value.primaryAction?.label ||
    content.value.ctaLabel ||
    content.value.buttonLabel ||
    ""
);
const ctaAction = computed(
  () =>
    content.value.primary_button?.action ||
    content.value.primaryAction?.action ||
    ""
);
const imageUrl = computed(() => content.value.image?.url || "");
const imageAlt = computed(() => content.value.image?.alt || "");
// dismissible defaults to true; only an explicit `false` suppresses the X and
// the overlay-click dismiss.
const isDismissible = computed(() => content.value.dismissible !== false);

// The overlay stays mounted in App.vue; the step prop arrives asynchronously
// (guide fetch), so "seen" is marked when the step first becomes available,
// not when the component mounts with a null step.
watch(
  () => props.step,
  (step) => {
    // This overlay is mounted once for the whole app, so the `isClosing` lock a
    // dismiss sets would otherwise outlive the step it locked: the next step of
    // a multi-step guide (or another guide after a client-side navigation)
    // would render with a disabled CTA/close button and an undismissable
    // overlay. A new step therefore always re-arms the controls.
    isClosing.value = false;

    // Mark the step seen exactly once (the SDK no-ops when already seen).
    if (step && typeof step.markAsSeen === "function") {
      step.markAsSeen();
    }
  },
  { immediate: true }
);

// A Knock CTA can name any internal path; the app's catch-all route matches
// every unknown path, so `route.matched.length > 0` alone is not proof the
// action is a real destination. Both apps name their catch-all / 404 route
// "NotFound", which no real route does — that is the reliable signal.
const isResolvableInternalPath = (action) => {
  if (!router || typeof router.resolve !== "function") return true;
  try {
    const resolved = router.resolve(action);
    const route = resolved?.route ?? resolved?.resolved ?? resolved;
    if (!route || !Array.isArray(route.matched) || route.matched.length === 0) {
      return false;
    }
    return route.name !== "NotFound";
  } catch (error) {
    return false;
  }
};

const logUnresolvableAction = (action) => {
  const guideKey = props.guide?.key || "unknown";
  Sentry.captureMessage(`Knock guide CTA points to an unresolvable route: ${action}`, {
    level: "error",
    tags: { component: "KnockGuideModal", guide: guideKey },
    extra: { action, guideKey },
  });
};

const navigate = (action) => {
  if (!action) return true;
  if (action.startsWith("/")) {
    // Internal route: SPA navigation. Storybook/tests without a router fall
    // back to a full-page location change so the CTA still works there.
    if (router && typeof router.push === "function") {
      if (!isResolvableInternalPath(action)) {
        return false;
      }
      router.push(action);
      return true;
    }
    window.location.href = action;
    return true;
  }
  window.open(action, "_blank", "noopener");
  return true;
};

const onDismiss = async () => {
  if (isClosing.value) return;
  // A non-dismissible guide ignores the overlay click (and has no X).
  if (!isDismissible.value) return;
  isClosing.value = true;
  try {
    // Dismissing archives the step via the SDK so it does not reappear (Eric).
    if (typeof props.step?.markAsArchived === "function") {
      await props.step.markAsArchived();
    }
  } finally {
    emit("dismiss");
  }
};

const onInteract = async () => {
  if (isClosing.value) return;
  isClosing.value = true;
  // ER-12090: an interacted guide is archived before navigating so it never
  // reappears on the next counter tick / store update (markAsInteracted alone
  // does not archive). Archiving is now deferred until navigation actually
  // succeeds so an unresolvable CTA cannot strand the user with a closed
  // announcement they can never reopen (ER-12344).
  if (typeof props.step?.markAsInteracted === "function") {
    await props.step.markAsInteracted();
  }

  const action = ctaAction.value;
  if (!navigate(action)) {
    logUnresolvableAction(action);
    // Re-arm the controls so the modal stays usable: the user can still dismiss
    // it, and a corrected link can be retried once the guide is updated.
    isClosing.value = false;
    return;
  }

  if (typeof props.step?.markAsArchived === "function") {
    await props.step.markAsArchived();
  }
  emit("interact");
};
</script>

<style scoped>
.knock-guide-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
}

.knock-guide-modal__card {
  width: 100%;
  max-width: 480px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 24px 48px -12px rgba(16, 24, 40, 0.18);
  overflow: hidden;
}

.knock-guide-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px 0;
}

.knock-guide-modal__eyebrow {
  color: #7f56d9;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.knock-guide-modal__close-btn {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #667085;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.knock-guide-modal__close-btn:hover {
  background: #f2f4f7;
  color: #475467;
}

.knock-guide-modal__close-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.knock-guide-modal__body {
  padding: 12px 24px 0;
}

.knock-guide-modal__title {
  margin: 0;
  color: #101828;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.knock-guide-modal__text {
  margin: 8px 0 0;
  color: #475467;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
}

/* Keep multi-paragraph rich-text bodies spaced like the previous single block
   instead of picking up default browser paragraph margins. */
.knock-guide-modal__text :deep(p) {
  margin: 0 0 8px;
}

.knock-guide-modal__text :deep(p:last-child) {
  margin-bottom: 0;
}

.knock-guide-modal__image {
  display: block;
  max-width: 100%;
  margin-top: 16px;
  border-radius: 8px;
}

.knock-guide-modal__footer {
  padding: 20px 24px 24px;
}

.knock-guide-modal__cta {
  width: 100%;
  height: 44px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background: #7f56d9;
  color: #ffffff;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.knock-guide-modal__cta:hover {
  background: #6941c6;
}

.knock-guide-modal__cta:focus-visible {
  outline: 2px solid #101828;
  outline-offset: 2px;
}

.knock-guide-modal__cta:disabled,
.knock-guide-modal__close-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.knock-guide-modal-enter-active,
.knock-guide-modal-leave-active {
  transition: opacity 0.2s ease;
}

.knock-guide-modal-enter-active .knock-guide-modal__card,
.knock-guide-modal-leave-active .knock-guide-modal__card {
  transition: transform 0.2s ease;
}

.knock-guide-modal-enter-from,
.knock-guide-modal-leave-to {
  opacity: 0;
}

.knock-guide-modal-enter-from .knock-guide-modal__card,
.knock-guide-modal-leave-to .knock-guide-modal__card {
  transform: scale(0.96);
}
</style>
